import type { ReactNode } from 'react'
import type { DeepSkyObject } from '@/constants/deep-sky'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Media } from '@/components/generals/media'

interface ObjectImageProps {
    object: DeepSkyObject
    className?: string
}

/** A real photograph where we have one; otherwise the star field drawn from the object's real colours. */
export const ObjectImage = ({ object, className }: ObjectImageProps): ReactNode => {
    const holderRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const holder = holderRef.current

        if (!object.procedural || !holder) return

        let cancelled = false

        void import('@/components/landing/eyepiece').then(({ drawProcedural }) => {
            if (cancelled || !object.procedural) return

            const canvas = drawProcedural(object.procedural, 640)

            canvas.className = 'absolute inset-0 h-full w-full'
            canvas.setAttribute('role', 'img')
            canvas.setAttribute('aria-label', `${object.name}, drawn from the real colours and positions of its stars`)
            holder.replaceChildren(canvas)
        })

        return () => {
            cancelled = true
        }
    }, [object])

    if (object.image) {
        return (
            <Media src={object.image} alt={`${object.name}, ${object.type.toLowerCase()} in ${object.constellation}`} sizes='(min-width: 1024px) 40vw, 100vw' className={cn('aspect-square w-full bg-black', className)} />
        )
    }

    return (
        <div className={cn('media media-grain relative aspect-square w-full bg-black', className)}>
            <div ref={holderRef} className='absolute inset-0' />
        </div>
    )
}
